// imports
import { ReactNode } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

interface CustomDialogProps {
  open: boolean;
  onClose: () => void;
  title: string | ReactNode;
  children: ReactNode;
  dataTestId?: string;
  actions?: ReactNode;
}

/**
 * CustomDialog
 *
 * Reusable dialog wrapper that ensures consistent styling across dialog titles, close buttons, and content area. Includes the ability to add custom content and actions.
 *
 * @param {CustomDialogProps} props - Custom defined dialog properties
 * @param {boolean} props.open - Whether the dialog is visible
 * @param {() => void} props.onClose - Called when the user closes the dialog
 * @param {string | ReactNode} props.title - The dialog's title
 * @param {ReactNode} props.children - The dialog's main content
 * @param {string} [props.dataTestId] - Optional test id
 * @param {ReactNode} [props.actions] - Optional buttons/elements located at the bottom of the dialog
 * @returns {JSX.Element} The rendered dialog element
 *
 * @example
 * <CustomDialog open={open} onClose={handleClose} title="My Dialog Title" dataTestId="my-dialog-id" actions={<Button>OK</Button>}>
 *   <Typography>Dialog body</Typography>
 * </CustomDialog>
 *
 */
export default function CustomDialog({
  open,
  onClose,
  title,
  children,
  dataTestId,
  actions,
}: CustomDialogProps) {
  return (
    <Dialog data-testid={dataTestId} open={open} onClose={onClose} sx={{zIndex: '5000'}}>
      <DialogTitle
        sx={{
          borderBottom: '1px solid',
          borderColor: (theme) => theme.palette.divider,
          py: 1.5,
          fontWeight: 'bold',
          fontSize: '1.5rem',
        }}
      >
        {title}
      </DialogTitle>
      <IconButton
        aria-label="Close dialog"
        onClick={onClose}
        sx={(theme) => ({
          position: 'absolute',
          right: 8,
          top: 12,
          color: theme.palette.grey[900],
        })}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent sx={{ pb: 1 }}>{children}</DialogContent>
      {actions && (
        <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'start' }}>
          {actions}
        </DialogActions>
      )}
    </Dialog>
  );
}
